import { useAuthPersonInfo, useDeleteAuthPerson } from '@domains/customer-people'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { ModalAreYouSureWithBody } from 'components/Modals'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export const AuthPersonPage = () => {
  const { authPersonId } = useParams<'authPersonId'>()
  const [deleted, setDeleted] = useState(false)
  const { data: authPersonInfo, isLoading, isError } = useAuthPersonInfo(deleted ? undefined : authPersonId)
  const deleteAuthPerson = useDeleteAuthPerson()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const notify = useMaterialNotification()
  const navigate = useNavigate()

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      notify(`Copied ${label} to clipboard`, 'info')
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !authPersonInfo) {
    return (
      <Box sx={{ pt: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Auth Person
        </Typography>
        <Typography color="error">Failed to load auth person info.</Typography>
      </Box>
    )
  }

  const handleDelete = () => {
    if (!authPersonId) return
    setDeleted(true)
    deleteAuthPerson.mutate(authPersonId, {
      onSuccess: () => {
        notify('Auth person deleted successfully', 'info')
        navigate(-1)
      },
      onError: () => setDeleted(false),
      onSettled: () => setShowDeleteModal(false),
    })
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Auth Person
      </Typography>
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          color: 'text.secondary',
        }}
      >
        {authPersonId}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3, maxWidth: 400 }}>
        <InfoRow label="Recovery Email" value={authPersonInfo.recoveryEmail ?? '-'} />
        <InfoRow label="Recovery Phone Number" value={authPersonInfo.recoveryPhoneNumber ?? '-'} />
        <InfoRow label="Uses BankID" value={<BooleanChip value={authPersonInfo.usesBankId} />} />
        <InfoRow label="Uses Passkey" value={<BooleanChip value={authPersonInfo.usesPasskey} />} />
        <InfoRow label="Uses Email/Password" value={<BooleanChip value={authPersonInfo.usesEmailPassword} />} />
        <InfoRow label="Identity Domain" value={authPersonInfo.identityDomain} />
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Connected Persons
        </Typography>
        {authPersonInfo.persons.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No connected persons.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Customer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {authPersonInfo.persons.map((person) => (
                  <TableRow key={person.personId}>
                    <TableCell
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                      onClick={() => copyToClipboard(person.personId, 'person ID')}
                    >
                      {person.name}
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                      onClick={() => copyToClipboard(person.customerId, 'customer ID')}
                    >
                      {person.customerName}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Button variant="contained" color="error" onClick={() => setShowDeleteModal(true)}>
          Delete Auth Person
        </Button>
      </Box>

      {showDeleteModal && (
        <ModalAreYouSureWithBody
          onClose={() => setShowDeleteModal(false)}
          onContinue={handleDelete}
          title="Delete Auth Person"
          bodyText={
            <>
              Are you sure you want to delete this auth person? The following persons will lose access:
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                {authPersonInfo.persons.map((person) => (
                  <li key={person.personId}>
                    {person.name} ({person.customerName})
                  </li>
                ))}
              </ul>
              <strong style={{ color: '#d32f2f' }}>This action cannot be undone.</strong>
            </>
          }
          confirmText="Yes, delete"
          confirmColor="error"
          loading={deleteAuthPerson.isLoading}
        />
      )}
    </Box>
  )
}

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    {typeof value === 'string' ? <Typography variant="body2">{value}</Typography> : value}
  </Box>
)

const BooleanChip = ({ value }: { value: boolean }) => (
  <Chip label={value ? 'Yes' : 'No'} color={value ? 'success' : 'default'} size="small" />
)
